import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaRegistrationFeesPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Registration Fees & Taxes</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Vehicle Registration Fees & Taxes</h1>
        <p className="text-obsidian-300">Breakdown of all Nevada DMV registration fees, governmental services tax, and other charges.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Base Registration Fees</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Vehicle Type</th><th className="text-right py-2 font-semibold text-obsidian-900">Base Fee</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[
                  ['Passenger Cars & Motorcycles', '$33.00'],
                  ['Trailers (1,000 lbs or less)', '$12.00'],
                  ['Trailers (over 1,000 lbs)', '$24.00'],
                  ['Mopeds', '$33.00'],
                  ['Off-Highway Vehicles (1-year decal)', '$20.00'],
                  ['Off-Highway Vehicles (3-year decal)', '$60.00'],
                ].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Governmental Services Tax (GST)</h2>
          <p>The Governmental Services Tax is calculated based on the vehicle&apos;s original Manufacturer&apos;s Suggested Retail Price (MSRP) and depreciates over time. This is typically the largest component of your registration cost.</p>
          <div className="mt-4 bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Vehicle Age</th><th className="text-right py-2 font-semibold text-obsidian-900">% of MSRP Used</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[
                  ['1st year', '35%'],
                  ['2nd year', '28%'],
                  ['3rd year', '21%'],
                  ['4th year', '14%'],
                  ['5th year', '7%'],
                  ['6th year and older', 'Minimum $6.00'],
                ].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-obsidian-400 mt-3">GST rate is $0.04 per $1 of assessed value. Use the DMV Online Fee Estimator at dmv.nv.gov for precise calculations.</p>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">License Plate Fees</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Plate Type</th><th className="text-right py-2 font-semibold text-obsidian-900">Fee</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[
                  ['Standard Plates (pair)', '$7.00'],
                  ['Specialty/Charitable Plates (initial)', '$61.00'],
                  ['Specialty/Charitable Plates (renewal)', '$30.00'],
                  ['Personalized Plates (initial)', '$96.00'],
                  ['Personalized Plates (renewal)', '$50.00'],
                  ['Duplicate Plate', '$6.00'],
                ].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Title & Transfer Fees</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Transaction</th><th className="text-right py-2 font-semibold text-obsidian-900">Fee</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[
                  ['Title Transfer (complete change of ownership)', '$28.25'],
                  ['Duplicate Title (mailed to Nevada address)', '$20.00'],
                  ['Expedited Title Processing', 'Additional fee — varies'],
                  ['VIN Inspection', '$1.00'],
                  ['Duplicate Registration Certificate', '$5.00'],
                ].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Questions About Your Fees?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can help you understand exactly what you&apos;ll owe and handle all payments on your behalf.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaRegistrationFeesPage;
